import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCustomDomains, updateDomainStatus } from "@/api/admin.api";
import type { CustomDomainVenue } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Globe, CheckCircle2, Clock, XCircle, ShieldCheck, ShieldX } from "lucide-react";

const STATUS_CONFIG = {
  pending: { label: "SSL Bekliyor", variant: "secondary" as const, icon: Clock },
  active: { label: "Aktif", variant: "default" as const, icon: CheckCircle2 },
  failed: { label: "Başarısız", variant: "destructive" as const, icon: XCircle },
};

function DomainRow({ venue }: { venue: CustomDomainVenue }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (status: "pending" | "active" | "failed") => updateDomainStatus(venue._id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-custom-domains"] }),
  });

  const cfg = STATUS_CONFIG[venue.customDomainStatus || "pending"];
  const Icon = cfg.icon;

  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-4">
        <Globe className="size-5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`https://${venue.customDomain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-sm hover:underline"
            >
              {venue.customDomain}
            </a>
            <Badge variant={cfg.variant} className="gap-1 text-xs">
              <Icon className="size-3" />
              {cfg.label}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {venue.name} ({venue.slug}) — {venue.userId?.name} ({venue.userId?.email})
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {venue.customDomainStatus !== "active" && (
            <Button
              size="sm"
              variant="default"
              className="gap-1.5"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate("active")}
            >
              <ShieldCheck className="size-3.5" />
              Aktif Et
            </Button>
          )}
          {venue.customDomainStatus !== "failed" && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate("failed")}
            >
              <ShieldX className="size-3.5" />
              Reddet
            </Button>
          )}
          {venue.customDomainStatus !== "pending" && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate("pending")}
            >
              <Clock className="size-3.5" />
              Beklet
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CustomDomainsPage() {
  const { data: venues, isLoading } = useQuery({
    queryKey: ["admin-custom-domains"],
    queryFn: getCustomDomains,
  });

  const pending = venues?.filter((v) => v.customDomainStatus === "pending") || [];
  const active = venues?.filter((v) => v.customDomainStatus === "active") || [];
  const failed = venues?.filter((v) => v.customDomainStatus === "failed") || [];

  return (
    <div className="max-w-3xl">
      <h1 className="mb-6 text-2xl font-bold">Custom Domain Istekleri</h1>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yükleniyor...</p>
      ) : !venues?.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Globe className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">Henüz custom domain isteği yok</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                Bekleyen ({pending.length})
              </h2>
              <div className="space-y-2">
                {pending.map((v) => <DomainRow key={v._id} venue={v} />)}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                SSL sertifikası almak icin: <code className="bg-muted px-1 rounded">sudo certbot --nginx -d domain.com</code>
              </p>
            </div>
          )}
          {active.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                Aktif ({active.length})
              </h2>
              <div className="space-y-2">
                {active.map((v) => <DomainRow key={v._id} venue={v} />)}
              </div>
            </div>
          )}
          {failed.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                Basarisiz ({failed.length})
              </h2>
              <div className="space-y-2">
                {failed.map((v) => <DomainRow key={v._id} venue={v} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
